import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { api, isMfaStep, type MfaStep, type SessionUser } from '../lib/api';
import { ErrorNote } from '../components/ui';
import { EnrolSteps, RecoveryCodes } from '../components/Controls';

/**
 * Sign-in — passwordless.
 *
 * Email in, six-digit code out by email, code in, session out. An invitation or
 * an owner-issued link arrives as `#/signin?t=…` and is redeemed on load. There
 * is no password anywhere on this screen, by design: a password is a shared
 * secret that lives in chat threads; a code that dies in ten minutes does not.
 *
 * The page is built around the one thing this product is: an append-only,
 * hash-chained record. A column of ledger rows runs down the left — labelled
 * as an illustration, generated deterministically, never real data.
 *
 * What it must never say: that this is a registered Consent Manager under
 * S.6(7)–(9). It is not.
 */

interface LedgerRow { seq: number; hash: string; action: string; channel: string; ago: string }

const ACTIONS: Array<[string, number]> = [
  ['granted', 55], ['declined', 18], ['withdrawn', 8], ['notice_viewed', 12], ['identity_verified', 7],
];
const CHANNELS = ['web', 'cookie', 'pref_centre', 'api', 'refresh_campaign'];

function mulberry32(seed: number) {
  return () => {
    seed |= 0; seed = (seed + 0x6D2B79F5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function illustrativeLedger(count: number): LedgerRow[] {
  const rnd = mulberry32(20230811);
  const hex = '0123456789abcdef';
  const rows: LedgerRow[] = [];
  let minutes = 2;
  for (let i = 0; i < count; i += 1) {
    let hash = '';
    for (let k = 0; k < 12; k += 1) hash += hex[Math.floor(rnd() * 16)];
    let pick = rnd() * 100;
    let action = 'granted';
    for (const [name, weight] of ACTIONS) { if (pick < weight) { action = name; break; } pick -= weight; }
    minutes += 1 + Math.floor(rnd() * 9);
    rows.push({
      seq: 41_872 - i, hash, action,
      channel: CHANNELS[Math.floor(rnd() * CHANNELS.length)]!,
      ago: minutes < 60 ? `${minutes}m` : `${Math.floor(minutes / 60)}h ${minutes % 60}m`,
    });
  }
  return rows;
}

/** A `#/signin?t=TOKEN` fragment left by an invitation or issued link. */
function linkTokenFromHash(): string | null {
  const m = window.location.hash.match(/[?&]t=([A-Za-z0-9_-]{16,})/);
  return m ? m[1]! : null;
}

/** An error the identity provider round trip left as `#/signin?sso_error=…`. */
function ssoErrorFromHash(): string | null {
  const m = window.location.hash.match(/[?&]sso_error=([^&]*)/);
  return m ? decodeURIComponent(m[1]!) : null;
}

type SsoStart = { sso: false } | { sso: true; enforced: boolean; url: string };

async function ssoDiscover(email: string): Promise<SsoStart> {
  const r = await fetch(`/v1/auth/sso/discover?email=${encodeURIComponent(email.trim())}`);
  if (!r.ok) return { sso: false };
  return r.json() as Promise<SsoStart>;
}

/** One redemption per token, even when an effect runs twice (links are single use). */
const redeeming = new Map<string, Promise<SessionUser | MfaStep>>();
function redeemOnce(token: string): Promise<SessionUser | MfaStep> {
  let p = redeeming.get(token);
  if (!p) { p = api.consumeLink(token); redeeming.set(token, p); }
  return p;
}

type Step = 'email' | 'code' | 'link' | 'mfa' | 'enrol' | 'codes';

export default function Login({ onSignedIn }: { onSignedIn: (user: SessionUser) => void }) {
  const [step, setStep] = useState<Step>(() => (linkTokenFromHash() ? 'link' : 'email'));
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(() => ssoErrorFromHash());
  const [busy, setBusy] = useState(false);
  const [health, setHealth] = useState<'checking' | 'online' | 'unreachable'>('checking');
  const [version, setVersion] = useState<string>('');
  // Two-factor: the challenge from the first factor, the enrolment secret, the codes shown once.
  const [challenge, setChallenge] = useState<string | null>(null);
  const [enrolment, setEnrolment] = useState<{ secret: string; otpauthUri: string } | null>(null);
  const [mfaCode, setMfaCode] = useState('');
  const [recovery, setRecovery] = useState<{ user: SessionUser; codes: string[] } | null>(null);

  /** After the first factor: a session, or the two-factor step still owed. */
  async function afterFirstFactor(r: SessionUser | MfaStep) {
    if (!isMfaStep(r)) { onSignedIn(r); return; }
    setChallenge(r.challenge);
    setMfaCode('');
    if (r.mfa === 'verify') { setStep('mfa'); return; }
    setEnrolment(await api.mfaEnrolStart(r.challenge));
    setStep('enrol');
  }

  async function verifySecond(e: FormEvent) {
    e.preventDefault();
    if (!challenge) return;
    setBusy(true); setError(null);
    try {
      onSignedIn(await api.mfaVerify(challenge, mfaCode));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  }

  async function confirmEnrol(e: FormEvent) {
    e.preventDefault();
    if (!challenge) return;
    setBusy(true); setError(null);
    try {
      const r = await api.mfaEnrolConfirm(challenge, mfaCode);
      setRecovery({ user: r.user, codes: r.recoveryCodes });
      setStep('codes');
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  function startOver() {
    setStep('email'); setChallenge(null); setEnrolment(null); setMfaCode(''); setCode(''); setError(null); setNotice(null);
  }

  const rows = useMemo(() => illustrativeLedger(26), []);

  useEffect(() => {
    let cancelled = false;
    fetch('/health')
      .then((r) => r.json())
      .then((j: { status?: string; version?: string }) => {
        if (cancelled) return;
        setHealth(j.status === 'ok' ? 'online' : 'unreachable');
        setVersion(j.version ?? '');
      })
      .catch(() => { if (!cancelled) setHealth('unreachable'); });
    return () => { cancelled = true; };
  }, []);

  // Redeem an invitation / issued link the moment the page opens with one.
  useEffect(() => {
    const token = linkTokenFromHash();
    if (!token) return;
    let cancelled = false;
    setBusy(true);
    redeemOnce(token)
      .then(async (r) => {
        if (cancelled) return;
        window.history.replaceState(null, '', window.location.pathname + '#/');
        await afterFirstFactor(r);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : String(err));
        setStep('email');
        window.history.replaceState(null, '', window.location.pathname + '#/');
      })
      .finally(() => { if (!cancelled) setBusy(false); });
    return () => { cancelled = true; };
  }, [onSignedIn]);

  useEffect(() => {
    if (ssoErrorFromHash()) window.history.replaceState(null, '', window.location.pathname + '#/');
  }, []);

  async function singleSignOn() {
    setBusy(true); setError(null);
    try {
      const d = await ssoDiscover(email);
      if (!d.sso) { setError('Single sign-on is not set up for this email domain.'); setBusy(false); return; }
      window.location.assign(d.url);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  }

  async function sendCode(e: FormEvent) {
    e.preventDefault();
    setBusy(true); setError(null); setNotice(null);
    try {
      const d = await ssoDiscover(email).catch(() => ({ sso: false }) as SsoStart);
      if (d.sso && d.enforced) { window.location.assign(d.url); return; }
      const res = await api.requestCode(email);
      setNotice(res.message);
      setStep('code');
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function verify(e: FormEvent) {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      await afterFirstFactor(await api.verifyCode(email, code));
      setBusy(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  }

  async function resend() {
    setBusy(true); setError(null);
    try {
      const res = await api.requestCode(email);
      setNotice(`New code sent. ${res.message}`);
      setCode('');
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="lg">
      <div className="lg-grain" aria-hidden />

      <section className="lg-ledger" aria-hidden>
        <div className="lg-ledger-head">
          <span className="lg-k">seq</span>
          <span className="lg-k">event_hash</span>
          <span className="lg-k">action</span>
          <span className="lg-k">channel</span>
          <span className="lg-k lg-k-right">age</span>
        </div>
        <div className="lg-ledger-scroll">
          <div className="lg-ledger-track">
            {[...rows, ...rows].map((r, i) => (
              <div className={`lg-row lg-row-${r.action}`} key={i}>
                <span className="lg-seq">{r.seq}</span>
                <span className="lg-hash">{r.hash}…</span>
                <span className="lg-action">{r.action}</span>
                <span className="lg-chan">{r.channel}</span>
                <span className="lg-age">{r.ago}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="lg-ledger-foot">Illustrative</div>
      </section>

      <main className="lg-main">
        <header className="lg-brand">
          <img src="./wekalp-logo-white.png" alt="Wekalp" className="lg-logo" />
          <span className="lg-module">Consent</span>
        </header>

        <div className="lg-copy">
          <h1 className="lg-title">
            <span className="lg-line">Consent</span>
            <span className="lg-line">management</span>
          </h1>
          <p className="lg-sub">
            Notice, consent and withdrawal records under the DPDP Act, 2023, with a hash-chained
            ledger and signed evidence exports.
          </p>
        </div>

        {step === 'mfa' ? (
          <form className="lg-card" onSubmit={verifySecond} noValidate>
            <div className="lg-card-head">
              <h2>Two-factor sign-in</h2>
              <HealthPill health={health} version={version} />
            </div>
            <label className="lg-field">
              <span>Six digits from your authenticator app, or a recovery code</span>
              <input
                className="lg-code" autoComplete="one-time-code" required autoFocus maxLength={11}
                value={mfaCode} placeholder="······"
                onChange={(e) => setMfaCode(e.target.value.trim())}
              />
            </label>
            <ErrorNote error={error} />
            <button className="lg-submit" type="submit" disabled={busy || mfaCode.length < 6}>
              <span>{busy ? 'Checking…' : 'Sign in'}</span>
              <span className="lg-submit-arrow" aria-hidden>→</span>
            </button>
            <div className="lg-links">
              <button type="button" className="lg-textbtn" onClick={startOver}>Start again</button>
            </div>
            <p className="lg-help">Lost the phone? Use a recovery code, or ask an owner to reset two-factor sign-in.</p>
          </form>
        ) : step === 'enrol' && enrolment ? (
          <form className="lg-card lg-card-wide" onSubmit={confirmEnrol} noValidate>
            <div className="lg-card-head">
              <h2>Set up two-factor sign-in</h2>
              <HealthPill health={health} version={version} />
            </div>
            <p className="lg-help">Your organisation requires a code from an authenticator app at each sign-in.</p>
            <div className="lg-enrol">
              <EnrolSteps secret={enrolment.secret} otpauthUri={enrolment.otpauthUri} code={mfaCode} setCode={setMfaCode} />
            </div>
            <ErrorNote error={error} />
            <button className="lg-submit" type="submit" disabled={busy || mfaCode.length !== 6}>
              <span>{busy ? 'Checking…' : 'Turn on and sign in'}</span>
              <span className="lg-submit-arrow" aria-hidden>→</span>
            </button>
            <div className="lg-links">
              <button type="button" className="lg-textbtn" onClick={startOver}>Start again</button>
            </div>
          </form>
        ) : step === 'codes' && recovery ? (
          <div className="lg-card lg-card-wide">
            <div className="lg-card-head">
              <h2>Save your recovery codes</h2>
              <HealthPill health={health} version={version} />
            </div>
            <div className="lg-enrol"><RecoveryCodes codes={recovery.codes} /></div>
            <button className="lg-submit" type="button" onClick={() => onSignedIn(recovery.user)}>
              <span>I have saved the codes</span>
              <span className="lg-submit-arrow" aria-hidden>→</span>
            </button>
          </div>
        ) : step === 'link' ? (
          <div className="lg-card">
            <div className="lg-card-head">
              <h2>Opening your invitation</h2>
              <HealthPill health={health} version={version} />
            </div>
            <p className="lg-help">Signing you in.</p>
            <ErrorNote error={error} />
          </div>
        ) : step === 'email' ? (
          <form className="lg-card" onSubmit={sendCode} noValidate>
            <div className="lg-card-head">
              <h2>Sign in</h2>
              <HealthPill health={health} version={version} />
            </div>

            <label className="lg-field">
              <span>Email</span>
              <input
                type="email" value={email} autoComplete="username" required autoFocus
                placeholder="you@company.example"
                onChange={(e) => setEmail(e.target.value)}
              />
            </label>

            <ErrorNote error={error} />

            <button className="lg-submit" type="submit" disabled={busy || !email.includes('@')}>
              <span>{busy ? 'Sending…' : 'Send sign-in code'}</span>
              <span className="lg-submit-arrow" aria-hidden>→</span>
            </button>

            <div className="lg-links">
              <button type="button" className="lg-textbtn" onClick={singleSignOn} disabled={busy || !email.includes('@')}>
                Continue with single sign-on
              </button>
            </div>

            <p className="lg-help">
              A six-digit code is sent to this address. Accounts are created by an administrator.
            </p>
          </form>
        ) : (
          <form className="lg-card" onSubmit={verify} noValidate>
            <div className="lg-card-head">
              <h2>Enter the code</h2>
              <HealthPill health={health} version={version} />
            </div>

            {notice && <div className="lg-notice" role="status">{notice}</div>}

            <label className="lg-field">
              <span>Six digits, sent to <strong>{email}</strong></span>
              <input
                className="lg-code"
                inputMode="numeric" pattern="[0-9]*" maxLength={6} autoComplete="one-time-code"
                value={code} required autoFocus placeholder="······"
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              />
            </label>

            <ErrorNote error={error} />

            <button className="lg-submit" type="submit" disabled={busy || code.length !== 6}>
              <span>{busy ? 'Checking…' : 'Sign in'}</span>
              <span className="lg-submit-arrow" aria-hidden>→</span>
            </button>

            <div className="lg-links">
              <button type="button" className="lg-textbtn" onClick={resend} disabled={busy}>
                Send another code
              </button>
              <button
                type="button" className="lg-textbtn"
                onClick={() => { setStep('email'); setCode(''); setNotice(null); setError(null); }}
              >
                Use a different email
              </button>
            </div>
            <p className="lg-help">Code valid for 10 minutes, single use.</p>
          </form>
        )}

        <footer className="lg-foot">
          <span>Deployed within the data fiduciary's own infrastructure.</span>
          <span>Not a registered Consent Manager under S.6(7) of the DPDP Act, 2023.</span>
        </footer>
      </main>
    </div>
  );
}

function HealthPill({ health, version }: { health: 'checking' | 'online' | 'unreachable'; version: string }) {
  return (
    <span className={`lg-health lg-health-${health}`} role="status">
      <i />
      {health === 'checking' && 'checking'}
      {health === 'online' && `online${version ? ` · v${version}` : ''}`}
      {health === 'unreachable' && 'API unreachable'}
    </span>
  );
}
